import { Component, Input, OnChanges, SimpleChanges, OnDestroy, ChangeDetectionStrategy, EventEmitter, Output } from '@angular/core';
import { isEqual } from 'lodash-es';
import moment, { Duration } from 'moment';
import { Observable, BehaviorSubject, combineLatest, Subscription, of } from 'rxjs';
import { map, debounceTime, filter, catchError, tap, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { AudioServiceFactory } from '@nimbus-library/features/shared/services/media/audio.service-factory';
import { IAudioService, PlaybackState } from '@nimbus-library/features/shared/services/media/i-audio.service';

enum LoadingStatus {
  Success = 1,
  Loading = 2,
  Failed = 3,
}

export enum MediaPlayerMode {
  Normal = 1,
  Preview = 2,
  OnDemand = 3,
}

@Component({
  standalone: false,
  selector: 'nimbus-input-media-player',
  templateUrl: './input-media-player.component.html',
  styleUrls: ['./input-media-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputMediaPlayerComponent implements OnChanges, OnDestroy {
  @Input()
  public withBorder: boolean = false;
  @Input()
  public source: Blob | File | string | string[];
  @Input()
  public disabled: boolean;
  @Input()
  public slidingThrottle: moment.Duration = moment.duration(0.5, 'seconds');
  @Input()
  public loading = false;
  @Input()
  public autoPlay = false;
  @Input()
  public mode: MediaPlayerMode = MediaPlayerMode.Normal;
  @Input()
  public metaForLaziness: { duration: string | moment.Duration };
  @Input()
  public isNeedBrowserScale: boolean = false;
  @Input()
  public stopEventPropagation: boolean = false;
  @Input()
  public isFakeEnabled: boolean = false;
  @Input()
  public invalidateCache: boolean = false;
  @Input()
  public hideSpinner: boolean = false;
  @Input()
  public showReloadButton: boolean = false;
  @Input()
  public errorMessage: string = TranslationKeys.GeneralMediaPlayerResourceLoadingFailed;

  @Output()
  public playMediaRequest = new EventEmitter();
  @Output()
  public isValidChanged = new EventEmitter<boolean>();
  @Output()
  public reload = new EventEmitter();

  private slidingSeconds$: BehaviorSubject<number | null>;
  private audioService: IAudioService;

  public TranslationKeys = TranslationKeys;
  public LoadingStatus = LoadingStatus;
  public mediaPlayerMode = MediaPlayerMode;
  public icons = ICONS;
  public playbackState: PlaybackState;

  public durationSeconds$: Observable<number>;
  public duration$: Observable<moment.Duration>;
  public elapsedSeconds$: Observable<number>;
  public elapsed$: Observable<moment.Duration>;
  public loadStatus$: Observable<LoadingStatus>;
  public isPlaying$: Observable<boolean>;
  public subscription$: Subscription;
  public noMedia$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public constructor(private readonly audioServiceFactory: AudioServiceFactory) {
    this.slidingSeconds$ = new BehaviorSubject<number | null>(null);
  }

  public ngOnChanges(change: SimpleChanges): void {
    if (change.source && !isEqual(change.source.currentValue, change.source.previousValue)) {
      this.noMedia$.next(!this.source);
      if (this.mode === MediaPlayerMode.Preview && this.noMedia$.value) {
        this.destroyAudioService();
        this.initPreview();

        this.subscription$ && this.subscription$.unsubscribe();
        this.subscription$ = new Subscription();
      } else if (this.mode === MediaPlayerMode.OnDemand && this.metaForLaziness) {
        this.destroyAudioService();
        this.initLazyMode();

        this.subscription$ && this.subscription$.unsubscribe();
        this.subscription$ = new Subscription();
      } else {
        this.createAudioService();
      }
    }

    if (change.disabled && change.disabled.currentValue) {
      this.stop(null, true);
    }
  }

  public ngOnDestroy(): void {
    this.destroyAudioService();
  }

  public durationMapper(seconds: number): Duration {
    return moment.duration(seconds, 'seconds');
  }

  public play(ev: MouseEvent): void {
    if (this.stopEventPropagation && ev) {
      ev.stopPropagation();
    }

    if (this.disabled) {
      return;
    }

    if (!this.audioService) {
      if (this.mode === MediaPlayerMode.OnDemand) {
        this.createAudioService();
      } else {
        this.playMediaRequest.emit();
      }
      return;
    }

    if (this.playbackState === PlaybackState.Play) {
      this.audioService.pause();
    } else {
      this.audioService.play();
    }
  }

  public stop(ev: MouseEvent, force: boolean = false): void {
    if (this.stopEventPropagation && ev) {
      ev.stopPropagation();
    }

    if (this.disabled && !force) {
      return;
    }
    if (this.audioService) {
      this.audioService.reset();
    }
  }

  public onSliding(seconds: number): void {
    this.slidingSeconds$.next(seconds);
  }

  public doReload(): void {
    this.reload.emit();
  }

  private onSlidingEnd(seconds: number): void {
    this.audioService.setCurrentTime(seconds);
    this.slidingSeconds$.next(null);
  }

  private initPreview(): void {
    this.loadStatus$ = of(LoadingStatus.Success);
    this.isValidChanged.emit(true);
    this.isPlaying$ = of(false);
  }

  private initLazyMode(): void {
    this.loadStatus$ = of(LoadingStatus.Success);
    this.isValidChanged.emit(true);
    this.isPlaying$ = of(false);
    this.elapsed$ = this.slidingSeconds$.pipe(map((seconds) => this.durationMapper(seconds)));
    this.autoPlay = true;
    const duration = moment.isDuration(this.metaForLaziness.duration)
      ? this.metaForLaziness.duration
      : moment.duration(this.metaForLaziness.duration);
    this.durationSeconds$ = of(duration.asSeconds());
    this.duration$ = of(duration);
  }

  private createAudioService(): void {
    if (typeof this.source === 'string' && this.invalidateCache) {
      this.source = this.source.includes('?')
        ? `${this.source}&invalidateCache=${Date.now()}`
        : `${this.source}?invalidateCache=${Date.now()}`;
    }

    const fileUrl: string | string[] =
      this.source && typeof this.source !== 'string' && !Array.isArray(this.source)
        ? window.URL.createObjectURL(this.source)
        : (this.source as string | string[]);
    const newAudioService = this.audioServiceFactory.getAudioService(fileUrl);

    if (this.audioService === newAudioService) {
      return;
    }

    this.destroyAudioService();
    this.audioService = newAudioService;

    this.subscription$ && this.subscription$.unsubscribe();
    this.subscription$ = new Subscription();

    this.initAudioService();
  }

  private initAudioService(): void {
    this.elapsedSeconds$ = combineLatest([this.audioService.elapsedSeconds$, this.slidingSeconds$]).pipe(
      map(([elapsed, slidingSeconds]) => (slidingSeconds === null ? elapsed : slidingSeconds)),
    );
    this.elapsed$ = this.elapsedSeconds$.pipe(map((seconds) => this.durationMapper(seconds)));

    this.loadStatus$ = this.audioService.loaded$.pipe(
      distinctUntilChanged(isEqual),
      map((loaded) => (loaded ? LoadingStatus.Success : LoadingStatus.Loading)),
      tap((loadingStatus) => {
        if (loadingStatus === LoadingStatus.Success) {
          this.durationSeconds$ = this.audioService.duration$;
          this.duration$ = this.audioService.duration$.pipe(map((duration) => this.durationMapper(duration)));
          if (this.autoPlay) {
            if (this.slidingSeconds$.value) {
              this.audioService.setCurrentTime(this.slidingSeconds$.value);
            }
            this.play(null);
          }
        }
      }),
      catchError(() => of(LoadingStatus.Failed)),
      shareReplay(1),
    );

    this.subscription$.add(
      this.loadStatus$.pipe(filter((status) => status !== LoadingStatus.Loading)).subscribe((status) => {
        this.isValidChanged.next(status !== LoadingStatus.Failed);
      }),
    );
    this.subscription$.add(this.audioService.state$.subscribe((state) => (this.playbackState = state)));
    this.subscription$.add(
      this.slidingSeconds$
        .pipe(
          filter((seconds) => seconds !== null),
          debounceTime(this.slidingThrottle.asMilliseconds()),
        )
        .subscribe((seconds) => {
          this.onSlidingEnd(seconds);
        }),
    );
    this.isPlaying$ = this.audioService.state$.pipe(map((state) => state === PlaybackState.Play));
  }

  private destroyAudioService(): void {
    if (this.audioService) {
      this.audioService.reset();
      this.audioService.ngOnDestroy();
      this.audioService = null;
    }
    if (this.subscription$) {
      this.subscription$.unsubscribe();
    }
  }
}
